import {isPlatformBrowser} from '@angular/common';
import {Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {Subscription} from 'rxjs';
import {ResultListApiModel} from '../../../api/models/result-list.api.model';
import {NotificationInterface} from '../../../shared/interfaces/notification.interface';
import {NotificationDataProviderService} from '../../services/notification.data-provider.service';

@Component({
  selector: 'app-mobile-notifications',
  templateUrl: './mobile-notifications.component.html',
  styleUrls: ['./mobile-notifications.component.scss'],
})
export class MobileNotificationsComponent implements OnInit, OnDestroy {
  public notifications: NotificationInterface[] = [];
  public loading = true;
  public loadingMore = false;
  public hasMore = true;
  public readonly pageRows = 30;
  private readonly subscriptions = new Subscription();
  private currentPage = 1;

  constructor(
    private readonly notificationDataProvider: NotificationDataProviderService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {
  }

  public ngOnInit(): void {
    this.loadFirstPage();
  }

  @HostListener('window:scroll')
  public onWindowScroll(): void {
    if (!isPlatformBrowser(this.platformId) || this.loading || this.loadingMore || !this.hasMore) {
      return;
    }

    const documentElement = document.documentElement;
    const scrollTop = window.scrollY || documentElement.scrollTop || document.body.scrollTop || 0;
    const documentHeight = Math.max(
      document.body.scrollHeight,
      documentElement.scrollHeight,
      document.body.offsetHeight,
      documentElement.offsetHeight,
    );

    if (scrollTop + window.innerHeight >= documentHeight - 240) {
      this.loadNextPage();
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public markAllAsRead(): void {
    const unread = this.notifications.filter((notification) => notification.readAt === null);
    if (!unread.length) {
      return;
    }
    this.subscriptions.add(this.notificationDataProvider.massUpdateReadAt(unread).subscribe((result) => {
      this.notifications = result.records || [];
      this.currentPage = 1;
      this.hasMore = this.resolveHasMore(result);
    }));
  }

  public get hasUnread(): boolean {
    return this.notifications.some((notification) => notification.readAt === null);
  }

  public removeResolvedNotification(id: number): void {
    this.notifications = this.notifications.filter(notification => notification.id !== id);
  }

  private loadFirstPage(): void {
    this.loading = true;
    this.subscriptions.add(this.notificationDataProvider.getCollection(1, this.pageRows).subscribe({
      next: (result) => {
        this.notifications = result.records || [];
        this.currentPage = 1;
        this.hasMore = this.resolveHasMore(result);
        this.loading = false;
      },
      error: () => this.loading = false,
    }));
  }

  private loadNextPage(): void {
    this.loadingMore = true;
    const nextPage = this.currentPage + 1;
    this.subscriptions.add(this.notificationDataProvider.getCollection(nextPage, this.pageRows).subscribe({
      next: (result) => {
        this.notifications = this.mergeNotifications(this.notifications, result.records || []);
        this.currentPage = nextPage;
        this.hasMore = this.resolveHasMore(result);
      },
      complete: () => this.loadingMore = false,
      error: () => this.loadingMore = false,
    }));
  }

  private resolveHasMore(result: ResultListApiModel<NotificationInterface>): boolean {
    // The notifications endpoint may omit the total (the model then exposes
    // its default value of 0 despite having records). Hypermedia next is
    // authoritative when available; otherwise a complete page signals that
    // another page may exist.
    if (result?.view && Object.prototype.hasOwnProperty.call(result.view, 'hydra:next')) {
      return !!result.view['hydra:next'];
    }
    return (result?.records || []).length >= this.pageRows;
  }

  private mergeNotifications(existing: NotificationInterface[], incoming: NotificationInterface[]): NotificationInterface[] {
    const notificationsById = new Map<number, NotificationInterface>();
    [...existing, ...incoming].forEach((notification) => {
      if (notification?.id) {
        notificationsById.set(notification.id, notification);
      }
    });

    return Array.from(notificationsById.values()).sort((a, b) => {
      const aCreatedAt = a?.createdAt ? new Date(a.createdAt as any).getTime() : 0;
      const bCreatedAt = b?.createdAt ? new Date(b.createdAt as any).getTime() : 0;
      return bCreatedAt - aCreatedAt || b.id - a.id;
    });
  }
}
